import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight, Hand } from "lucide-react";
import { ASSETS } from "@/lib/config";

const ITEMS = [
    { src: ASSETS.feedback1, tag: "TESTIMONI · 01", quote: "\"Pakai SUPERZONE sebelum CPI News, PACAK TERUS.\"" },
    { src: ASSETS.feedback2, tag: "TESTIMONI · 02", quote: "\"Lepas pakai SUPERZONE, saya merasa SETIAP HARI WD PROFIT.\"" },
    { src: ASSETS.feedback3, tag: "TESTIMONI · 03", quote: "\"Mapping memang PADU dari CIKO.\"" },
    { src: ASSETS.feedback4, tag: "TESTIMONI · 04", quote: "\"Suri Rumah pun boleh BUAT DUIT PROFIT.\"" },
    { src: ASSETS.feedback5, tag: "TESTIMONI · 05", quote: "\"Info News memang MEMBANTU.\"" },
];

function FrameCard({ it, i, testid }) {
    return (
        <motion.figure
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: i * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
            className="feedback-frame group"
            data-testid={testid}
        >
            <div className="flex items-center justify-between px-4 py-3 border-b-2 border-[#FAF9F6] bg-[#001b6b]">
                <span className="font-mono-tight text-[10px] tracking-[0.24em] uppercase">
                    {it.tag}
                </span>
                <span className="h-2 w-2 rounded-full bg-[#ED1C24] blink" />
            </div>
            <div className="relative bg-[#0044FE]">
                <img
                    src={it.src}
                    alt={`Feedback ${i + 1}`}
                    className="block w-full h-auto transition-transform duration-700 group-hover:scale-[1.03] select-none pointer-events-none"
                    loading="lazy"
                    draggable={false}
                />
            </div>
            <figcaption className="px-4 py-3 border-t-2 border-[#FAF9F6] bg-[#001b6b] font-display uppercase text-sm md:text-base tracking-wide">
                {it.quote}
            </figcaption>
        </motion.figure>
    );
}

export default function Feedback() {
    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: true,
        align: "center",
        skipSnaps: false,
    });
    const [selected, setSelected] = useState(0);
    const [total, setTotal] = useState(ITEMS.length);

    const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        const onSelect = () => {
            setSelected(emblaApi.selectedScrollSnap());
        };
        onSelect();
        setTotal(emblaApi.scrollSnapList().length);
        emblaApi.on("select", onSelect);
        emblaApi.on("reInit", onSelect);
    }, [emblaApi]);

    return (
        <section
            id="feedback"
            data-testid="feedback-section"
            className="relative bg-[#0044FE] text-[#FAF9F6] overflow-hidden"
        >
            <div className="absolute inset-0 bg-grid opacity-40" />
            {/* DESKTOP red gradient (original top-left) */}
            <div
                className="blob hidden md:block"
                style={{ background: "#ED1C24", width: 460, height: 460, top: "10%", left: "-10%" }}
            />
            {/* MOBILE red gradient — placed at bottom-right */}
            <div
                className="blob md:hidden"
                style={{ background: "#ED1C24", width: 360, height: 360, bottom: "10%", right: "-30%" }}
            />
            <div
                className="blob md:hidden"
                style={{ background: "#ED1C24", width: 240, height: 240, top: "5%", left: "-25%", opacity: 0.4 }}
            />

            <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-10 pt-24 md:pt-32 pb-24 md:pb-32">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <h2
                        className="font-display uppercase text-[12vw] sm:text-[9vw] md:text-[6.2vw] leading-[0.9] text-center md:text-left"
                        data-testid="feedback-headline"
                    >
                        FEEDBACK YANG DAH
                        <br />
                        <span className="text-outline">FOLLOW</span>{" "}
                        <span className="ciko-box">CIKO.</span>
                    </h2>
                    <div className="font-mono-tight text-xs uppercase tracking-widest text-[#FAF9F6]/70 md:pb-4 text-center md:text-right">
                        REAL PEOPLE &nbsp;/&nbsp; REAL PROFIT
                    </div>
                </div>

                {/* DESKTOP — vertical grid (3 per row, last row centered for 2 items) */}
                <div
                    className="hidden md:flex mt-16 gap-6 lg:gap-8 flex-wrap justify-center"
                    data-testid="feedback-desktop-grid"
                >
                    {ITEMS.map((it, i) => (
                        <div
                            key={i}
                            className="basis-[calc(33.333%-1rem)] lg:basis-[calc(33.333%-1.4rem)] min-w-0"
                        >
                            <FrameCard
                                it={it}
                                i={i}
                                testid={`feedback-card-${i}`}
                            />
                        </div>
                    ))}
                </div>

                {/* MOBILE — swipe instruction + Embla carousel */}
                <div className="md:hidden">
                    <div className="mt-10 flex items-center justify-center gap-3 text-[#FAF9F6]/85 font-mono-tight text-xs uppercase tracking-[0.24em]">
                        <Hand className="w-4 h-4 text-[#ED1C24]" />
                        <span data-testid="swipe-instruction">
                            Swipe → untuk lihat feedback lain
                        </span>
                        <ArrowLeft className="w-4 h-4" strokeWidth={2.5} />
                        <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                    </div>

                    <div className="mt-6 relative" data-testid="feedback-carousel">
                        <div
                            className="overflow-hidden cursor-grab active:cursor-grabbing"
                            ref={emblaRef}
                        >
                            <div className="flex touch-pan-y">
                                {ITEMS.map((it, i) => (
                                    <div
                                        key={i}
                                        className="flex-[0_0_88%] mr-4"
                                    >
                                        <FrameCard
                                            it={it}
                                            i={i}
                                            testid={`feedback-card-mobile-${i}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-6 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 font-mono-tight text-xs tracking-widest text-[#FAF9F6]/70">
                                <span data-testid="carousel-index">
                                    {String(selected + 1).padStart(2, "0")} /{" "}
                                    {String(total).padStart(2, "0")}
                                </span>
                                <div className="h-px w-24 bg-[#FAF9F6]/30 relative overflow-hidden">
                                    <span
                                        className="absolute top-0 left-0 h-px bg-[#ED1C24] transition-all duration-500"
                                        style={{
                                            width: `${((selected + 1) / total) * 100}%`,
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={scrollPrev}
                                    data-testid="carousel-prev"
                                    aria-label="Previous"
                                    className="w-11 h-11 border-2 border-[#FAF9F6] flex items-center justify-center hover:bg-[#FAF9F6] hover:text-[#0044FE] transition-colors"
                                >
                                    <ArrowLeft className="w-5 h-5" strokeWidth={2.5} />
                                </button>
                                <button
                                    onClick={scrollNext}
                                    data-testid="carousel-next"
                                    aria-label="Next"
                                    className="w-11 h-11 border-2 border-[#FAF9F6] bg-[#ED1C24] flex items-center justify-center hover:bg-[#ff2a33] transition-colors"
                                >
                                    <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Editorial marquee removed per iteration 5 */}
            </div>
        </section>
    );
}
